import { jsPDF } from 'jspdf';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

export interface ReportWorkstationItem {
  name: string;
  department: string;
  status: string;
  activeHours: number;
  idleIncidents: number;
  streakHours: number;
}

export interface ReportOptions {
  organizationName: string;
  periodName: string;
  generatedBy: string;
  workstations: ReportWorkstationItem[];
  metrics: {
    totalWorkstations: number;
    presenceRate: number;
    totalMonitoredHours: number;
    avgDailyHours: number;
  };
}

export async function generateExecutiveDossierPdf(options: ReportOptions): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  // Background Header Accent
  doc.setFillColor(15, 23, 42); // Dark slate
  doc.rect(0, 0, pageWidth, 38, 'F');

  // Blue Accent Bar
  doc.setFillColor(59, 130, 246); // Pulse blue
  doc.rect(0, 38, pageWidth, 2, 'F');

  // Title & Header Text
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('CK PULSE — EXECUTIVE PRESENCE DOSSIER', margin, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184); // Slate 400
  doc.text('ZERO-SPYWARE WORKSTATION TELEMETRY & ATTENDANCE AUDIT', margin, 23);

  // Top Right Meta Box
  doc.setFontSize(8);
  doc.setTextColor(203, 213, 225);
  doc.text(`ORGANIZATION: ${options.organizationName.toUpperCase()}`, pageWidth - margin, 14, { align: 'right' });
  doc.text(`PERIOD: ${options.periodName.toUpperCase()}`, pageWidth - margin, 20, { align: 'right' });
  doc.text(`GENERATED: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, pageWidth - margin, 26, { align: 'right' });
  doc.text(`AUDITOR: ${options.generatedBy.toUpperCase()}`, pageWidth - margin, 32, { align: 'right' });

  // Confidentiality Badge
  doc.setFillColor(239, 68, 68);
  doc.rect(margin, 46, 28, 5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('STRICTLY CONFIDENTIAL', margin + 2, 49.5);

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('This executive summary is generated cryptographically from edge heartbeat telemetry. Contains no screen captures or keylogs.', margin + 32, 49.5);

  // Executive KPI Summary Cards
  const cardY = 56;
  const cardWidth = (pageWidth - margin * 2 - 9) / 4;
  const cardHeight = 22;

  const kpis = [
    { label: 'WORKSTATIONS', value: `${options.metrics.totalWorkstations}`, sub: 'Active Endpoints', color: [16, 185, 129] },
    { label: 'PRESENCE RATE', value: `${options.metrics.presenceRate}%`, sub: 'Workforce SLA', color: [59, 130, 246] },
    { label: 'TOTAL ACTIVE TIME', value: `${options.metrics.totalMonitoredHours.toFixed(1)} hrs`, sub: 'Recorded Work', color: [139, 92, 246] },
    { label: 'AVG DAILY / USER', value: `${options.metrics.avgDailyHours.toFixed(1)} hrs`, sub: 'Daily Output', color: [245, 158, 11] },
  ];

  kpis.forEach((kpi, idx) => {
    const x = margin + idx * (cardWidth + 3);

    // Card background
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, cardY, cardWidth, cardHeight, 2, 2, 'F');

    // Left accent strip
    doc.setFillColor(kpi.color[0], kpi.color[1], kpi.color[2]);
    doc.roundedRect(x, cardY, 2, cardHeight, 1, 1, 'F');

    // Label
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text(kpi.label, x + 5, cardY + 6);

    // Value
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    doc.text(kpi.value, x + 5, cardY + 14);

    // Subtitle
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text(kpi.sub, x + 5, cardY + 19);
  });

  // Section Heading: Workstation Breakdown Table
  let currentY = cardY + cardHeight + 10;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text('Workstation Presence & Productivity Breakdown', margin, currentY);

  currentY += 4;

  // Table Headers
  const colX = {
    name: margin + 2,
    dept: margin + 60,
    status: margin + 100,
    hours: margin + 128,
    incidents: margin + 152,
    streak: margin + 172,
  };

  doc.setFillColor(241, 245, 249);
  doc.rect(margin, currentY, pageWidth - margin * 2, 7, 'F');

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(71, 85, 105);
  doc.text('MEMBER / WORKSTATION', colX.name, currentY + 4.8);
  doc.text('DEPARTMENT', colX.dept, currentY + 4.8);
  doc.text('STATUS', colX.status, currentY + 4.8);
  doc.text('ACTIVE TIME', colX.hours, currentY + 4.8);
  doc.text('IDLE ALERTS', colX.incidents, currentY + 4.8);
  doc.text('MAX STREAK', colX.streak, currentY + 4.8);

  currentY += 8;

  // Render Table Rows
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  options.workstations.forEach((w, index) => {
    // Check if new page is needed
    if (currentY > pageHeight - 35) {
      doc.addPage();
      currentY = 20;

      // Repeat Table Header
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, currentY, pageWidth - margin * 2, 7, 'F');
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(71, 85, 105);
      doc.text('MEMBER / WORKSTATION', colX.name, currentY + 4.8);
      doc.text('DEPARTMENT', colX.dept, currentY + 4.8);
      doc.text('STATUS', colX.status, currentY + 4.8);
      doc.text('ACTIVE TIME', colX.hours, currentY + 4.8);
      doc.text('IDLE ALERTS', colX.incidents, currentY + 4.8);
      doc.text('MAX STREAK', colX.streak, currentY + 4.8);
      currentY += 8;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
    }

    // Row alternating background
    if (index % 2 === 0) {
      doc.setFillColor(250, 250, 250);
      doc.rect(margin, currentY - 3.5, pageWidth - margin * 2, 7, 'F');
    }

    doc.setTextColor(15, 23, 42);
    doc.text(w.name.substring(0, 30), colX.name, currentY + 1);

    doc.setTextColor(100, 116, 139);
    doc.text((w.department || 'General').substring(0, 20), colX.dept, currentY + 1);

    // Status Pill
    if (w.status === 'ACTIVE') {
      doc.setTextColor(16, 185, 129);
      doc.text('ACTIVE', colX.status, currentY + 1);
    } else if (w.status === 'INACTIVE') {
      doc.setTextColor(239, 68, 68);
      doc.text('INACTIVE', colX.status, currentY + 1);
    } else if (w.status === 'BREAK') {
      doc.setTextColor(245, 158, 11);
      doc.text('ON BREAK', colX.status, currentY + 1);
    } else {
      doc.setTextColor(148, 163, 184);
      doc.text('OFFLINE', colX.status, currentY + 1);
    }

    doc.setTextColor(15, 23, 42);
    doc.text(`${w.activeHours.toFixed(1)} hrs`, colX.hours, currentY + 1);

    doc.setTextColor(w.idleIncidents > 2 ? 239 : 100, w.idleIncidents > 2 ? 68 : 116, w.idleIncidents > 2 ? 68 : 139);
    doc.text(`${w.idleIncidents}`, colX.incidents, currentY + 1);

    doc.setTextColor(15, 23, 42);
    doc.text(`${w.streakHours.toFixed(1)} hrs`, colX.streak, currentY + 1);

    currentY += 7.5;
  });

  // Privacy & Compliance Verification Box
  if (currentY > pageHeight - 45) {
    doc.addPage();
    currentY = 20;
  } else {
    currentY += 6;
  }

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, currentY, pageWidth - margin * 2, 22, 2, 2, 'FD');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('COMPLIANCE & PRIVACY CERTIFICATION', margin + 4, currentY + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    'This organization uses CK Pulse Zero-Spyware Presence Architecture. Workstation status is derived exclusively from hardware input timestamps.\nNo keystroke logs, audio recordings, or visual screen grabs are captured, ensuring strict compliance with GDPR, CCPA, and Labor Privacy Acts.',
    margin + 4,
    currentY + 11
  );

  // Signature Block
  const sigY = currentY + 30;
  if (sigY < pageHeight - 20) {
    doc.setDrawColor(203, 213, 225);
    doc.line(margin, sigY, margin + 55, sigY);
    doc.line(pageWidth - margin - 55, sigY, pageWidth - margin, sigY);

    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text('Authorized Executive Signature', margin, sigY + 4);
    doc.text('Audited Timestamp / Seal', pageWidth - margin - 55, sigY + 4);
  }

  // Footer on all pages
  const totalPageCount = (doc.internal as any).getNumberOfPages();
  for (let i = 1; i <= totalPageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `CK Pulse Executive Intelligence Dossier • Generated automatically via Cloudflare Edge • Page ${i} of ${totalPageCount}`,
      pageWidth / 2,
      pageHeight - 6,
      { align: 'center' }
    );
  }

  // Save or share the document
  const fileName = `CK-Pulse-Executive-Dossier-${new Date().toISOString().split('T')[0]}.pdf`;

  if (Capacitor.isNativePlatform()) {
    try {
      const dataUri = doc.output('datauristring');
      const base64Data = dataUri.split(',')[1];
      const writeResult = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Cache,
      });

      await Share.share({
        title: fileName,
        text: `CK Pulse Executive Dossier for ${options.organizationName}`,
        url: writeResult.uri,
        dialogTitle: 'Share or Open PDF Report',
      });
    } catch (nativeErr) {
      console.warn('Native PDF export failed, falling back to browser download:', nativeErr);
      doc.save(fileName);
    }
  } else {
    doc.save(fileName);
  }
}
